// Apollo prospecting inside the Leads module. Searches Apollo.io, imports
// selected results directly as CRM leads with source="Apollo".
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ExternalLink, Info } from "lucide-react";

export default function LeadsProspectApolloPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [title, setTitle] = useState("");
  const [company, setCompany] = useState("");
  const [industry, setIndustry] = useState("");
  const [location, setLocation] = useState("");
  const [results, setResults] = useState<any[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imported, setImported] = useState<{ created: number; skipped: number } | null>(null);

  const status = useQuery({
    queryKey: ["/apollo/status"],
    queryFn: async () => (await api.get("/apollo/status")).data as { configured: boolean; canSearch: boolean },
  });

  async function search() {
    setBusy(true); setError(null); setImported(null);
    try {
      const { data } = await api.post("/apollo/search-people", {
        person_titles: title ? [title] : undefined,
        q_organization_name: company || undefined,
        organization_industry_tag_ids: industry ? [industry] : undefined,
        person_locations: location ? [location] : undefined,
        page: 1,
        per_page: 25,
      });
      setResults(data.people ?? data.contacts ?? []);
      setSelected(new Set());
    } catch (err: any) {
      setError(err?.response?.data?.message ?? "Apollo search failed.");
      setResults(null);
    } finally { setBusy(false); }
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function importSelection() {
    if (!results) return;
    const ids = results.filter((p) => selected.has(p.id)).map((p) => p.id).slice(0, 25);
    if (ids.length === 0) { alert("Select at least one person to import."); return; }
    if (!confirm(
      `Apollo will enrich ${ids.length} ${ids.length === 1 ? "person" : "people"} — this uses ~${ids.length} credit${ids.length === 1 ? "" : "s"} from your plan. Continue?`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/apollo/import-leads", { apolloIds: ids });
      setImported({ created: data.created?.length ?? 0, skipped: data.skipped?.length ?? 0 });
      qc.invalidateQueries({ queryKey: ["/leads"] });
      setSelected(new Set());
    } catch (err: any) {
      setError(err?.response?.data?.message ?? "Import failed.");
    } finally { setBusy(false); }
  }

  // --- Not configured state ---
  if (status.isFetched && !status.data?.configured) {
    return (
      <>
        <PageHeader
          title="Prospect from Apollo"
          description="Apollo.io is not connected yet."
          actions={<Button asChild variant="outline"><Link to="/leads"><ArrowLeft className="mr-1 h-4 w-4"/>Back to Leads</Link></Button>}
        />
        <div className="p-6">
          <Card className="max-w-2xl">
            <CardContent className="space-y-3 p-6">
              <div className="flex items-start gap-3">
                <Info className="mt-0.5 h-5 w-5 text-amber-500" />
                <div>
                  <h3 className="font-semibold">Apollo API key required</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    To search Apollo's prospect database and import leads, add an API key:
                  </p>
                </div>
              </div>
              <ol className="ml-5 list-decimal space-y-2 text-sm">
                <li>
                  Get your key at{" "}
                  <a href="https://app.apollo.io/#/settings/integrations/api" target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                    Apollo → Settings → Integrations → API <ExternalLink className="h-3 w-3" />
                  </a>
                </li>
                <li>
                  Open <span className="font-mono rounded bg-muted px-1.5 py-0.5">backend/.env</span> and set{" "}
                  <span className="font-mono rounded bg-muted px-1.5 py-0.5">APOLLO_API_KEY=your_key_here</span>
                </li>
                <li>
                  Restart the backend — this page will activate automatically.
                </li>
              </ol>
              <p className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
                Without Apollo, you can still import leads via <Link to="/leads/import" className="text-primary hover:underline">CSV upload</Link> or the <Link to="/leads/prospect/linkedin" className="text-primary hover:underline">LinkedIn Lead Gen webhook</Link>.
              </p>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  // --- Active state ---
  return (
    <>
      <PageHeader
        title="Prospect from Apollo"
        description="Search Apollo's B2B database and import matching people as Leads."
        actions={<Button asChild variant="outline"><Link to="/leads"><ArrowLeft className="mr-1 h-4 w-4"/>Back to Leads</Link></Button>}
      />

      <div className="p-6 space-y-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Filters</CardTitle></CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2">
            <Input placeholder="Title (e.g. VP of Engineering)" value={title} onChange={(e) => setTitle(e.target.value)} />
            <Input placeholder="Company name" value={company} onChange={(e) => setCompany(e.target.value)} />
            <Input placeholder="Industry tag (e.g. Software, Healthcare)" value={industry} onChange={(e) => setIndustry(e.target.value)} />
            <Input placeholder="Location (e.g. San Francisco, United States)" value={location} onChange={(e) => setLocation(e.target.value)} />
            <div className="md:col-span-2 flex gap-2">
              <Button onClick={search} disabled={busy || (!title && !company && !industry && !location)}>
                {busy ? "Searching…" : "Search Apollo"}
              </Button>
              <Button variant="outline" onClick={() => { setTitle(""); setCompany(""); setIndustry(""); setLocation(""); setResults(null); }}>
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
        {imported && (
          <p className="rounded-md bg-primary/10 px-3 py-2 text-sm">
            Imported <span className="font-semibold">{imported.created}</span> leads.
            {imported.skipped > 0 && <> Skipped {imported.skipped} duplicates (already in Leads).</>}
            {" "}
            <Link to="/leads" className="text-primary hover:underline">View Leads →</Link>
          </p>
        )}

        {results && (
          <Card>
            <CardHeader className="flex-row items-center justify-between pb-2">
              <div>
                <CardTitle className="text-base">
                  Results ({results.length})
                  {selected.size > 0 && <span className="ml-2 text-xs font-normal text-muted-foreground">{selected.size} selected</span>}
                </CardTitle>
                <p className="mt-1 text-xs text-muted-foreground">
                  Last names and emails are hidden in the preview — they unlock when you import (Apollo charges ~1 credit per person). Phones require a follow-up enrichment from the Lead page.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (selected.size === results.length) setSelected(new Set());
                    else setSelected(new Set(results.map((p) => p.id)));
                  }}
                >
                  {selected.size === results.length ? "Clear" : "Select all"}
                </Button>
                <Button size="sm" onClick={importSelection} disabled={selected.size === 0 || busy}>
                  {busy ? "Enriching…" : `Import ${selected.size || ""} as leads`}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {results.length === 0 && (
                <p className="py-8 text-center text-sm text-muted-foreground">No matches. Try broader filters.</p>
              )}
              <ul className="divide-y">
                {results.map((p) => {
                  const lastName = p.last_name ?? p.last_name_obfuscated ?? "";
                  const displayName = p.name ?? ([p.first_name, lastName].filter(Boolean).join(" ").trim() || "Unknown");
                  const orgName = p.organization?.name ?? p.organization_name ?? null;
                  return (
                    <li key={p.id} className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-muted/40" onClick={() => toggle(p.id)}>
                      <input
                        type="checkbox"
                        checked={selected.has(p.id)}
                        onChange={() => toggle(p.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium">
                          {displayName}
                          {p.last_name_obfuscated && !p.last_name && (
                            <span className="ml-2 text-xs font-normal text-muted-foreground">(last name hidden)</span>
                          )}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">
                          {p.title ?? "—"}{orgName ? ` · ${orgName}` : ""}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                        {p.has_email && <Badge variant="secondary" className="text-[10px]">email ✓</Badge>}
                        {(p.has_direct_phone === "Yes" || p.has_mobile_phone || p.has_work_direct_phone) && (
                          <Badge variant="secondary" className="text-[10px]">phone ✓</Badge>
                        )}
                        {p.has_city && <Badge variant="outline" className="text-[10px]">location ✓</Badge>}
                        {p.linkedin_url && (
                          <a
                            href={p.linkedin_url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="hover:underline"
                          >
                            LinkedIn →
                          </a>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
}
